import { useEffect, useState } from 'react';

const parseLocaleNumber = (value) => {
    if (typeof value === 'number') return value;
    return Number(String(value).replace(',', '.'));
};

const formatLocaleNumber = (value, decimals = 2) => {
    return Number(value || 0).toFixed(decimals).replace('.', ',');
};

const getInitialFormState = (units) => ({
    id: null,
    name: '',
    description: '',
    stock: '0',
    threshold: '0',
    unit_id: units[0]?.id ? String(units[0].id) : '',
    purchasePrice: '0,00',
    category: '',
    is_active: true,
});

export default function ConsumablesTab({ consumables = [], units = [], onSaveConsumable, onDeleteConsumable }) {
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState(getInitialFormState(units));
    const [searchTerm, setSearchTerm] = useState('');

    const filteredConsumables = (consumables || []).filter((item) =>
        item.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );

    useEffect(() => {
        if (selectedId === null) {
            setForm(getInitialFormState(units));
            return;
        }

        const consumable = (consumables || []).find((item) => String(item.id) === String(selectedId));
        if (consumable) {
            setForm({
                ...getInitialFormState(units),
                id: consumable.id,
                name: consumable.name || '',
                description: consumable.description || '',
                stock: consumable.stock != null ? formatLocaleNumber(consumable.stock, 0) : '0',
                threshold: consumable.threshold != null ? formatLocaleNumber(consumable.threshold, 0) : '0',
                unit_id: consumable.unit_id != null ? String(consumable.unit_id) : (units[0]?.id ? String(units[0].id) : ''),
                purchasePrice: consumable.purchasePrice != null ? formatLocaleNumber(consumable.purchasePrice, 2) : '0,00',
                category: consumable.category || '',
                is_active: consumable.is_active ?? true,
            });
        }
    }, [selectedId, consumables, units]);

    useEffect(() => {
        if (!form.unit_id && units.length) {
            setForm((prev) => ({ ...prev, unit_id: units[0].id ? String(units[0].id) : '' }));
        }
    }, [form.unit_id, units]);

    const clearForm = () => {
        setSelectedId(null);
        setForm(getInitialFormState(units));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!form.name.trim()) return;

        onSaveConsumable?.({
            ...form,
            unit_id: form.unit_id ? Number(form.unit_id) : null,
            stock: parseLocaleNumber(form.stock),
            threshold: parseLocaleNumber(form.threshold),
            purchasePrice: parseLocaleNumber(form.purchasePrice),
        });

        clearForm();
    };

    return (
        <div className="flex flex-col h-screen space-y-6">
            <section className="rounded-3xl border border-green-400 bg-green-50/90 p-5 shadow-xl shadow-slate-950/20">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-green-400">Consommables</p>
                        <h2 className="text-3xl font-semibold text-green-900">Gestion des consommables de stock</h2>
                    </div>
                    <p className="max-w-xl text-sm text-green-600">
                        Créez, modifiez et suivez les consommables utilisés dans les bons de commande et le stock.
                    </p>
                </div>
            </section>

            <div className="flex gap-5 flex-1 overflow-hidden">
                <div className="w-1/3 rounded-3xl border border-green-400 bg-white p-5 shadow-xl shadow-slate-950/20 overflow-y-auto">
                    <h3 className="mb-4 text-xl font-semibold text-green-900">{selectedId ? 'Modifier un consommable' : 'Ajouter un consommable'}</h3>
                    <form className="space-y-4" onSubmit={handleSubmit}>
                        <div className="grid gap-4 grid-cols-1">
                            <div>
                                <label className="text-sm text-green-600">Nom *</label>
                                <input
                                    value={form.name}
                                    onChange={(event) => setForm({ ...form, name: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Ex. Papier toilette"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Description</label>
                                <textarea
                                    value={form.description}
                                    onChange={(event) => setForm({ ...form, description: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    rows="3"
                                    placeholder="Détails du produit"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Catégorie</label>
                                <input
                                    value={form.category}
                                    onChange={(event) => setForm({ ...form, category: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Nettoyage, entretien..."
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Stock</label>
                                <input
                                    value={form.stock}
                                    onChange={(event) => setForm({ ...form, stock: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Unité</label>
                                <select
                                    value={form.unit_id ?? ''}
                                    onChange={(event) => setForm({ ...form, unit_id: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                >
                                    {units.map((unit) => (
                                        <option key={unit.id} value={String(unit.id)}>{unit.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Seuil d'alerte</label>
                                <input
                                    value={form.threshold}
                                    onChange={(event) => setForm({ ...form, threshold: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Prix d'achat (Ar)</label>
                                <input
                                    value={form.purchasePrice}
                                    onChange={(event) => setForm({ ...form, purchasePrice: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="0,00"
                                />
                            </div>
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={form.is_active}
                                    onChange={(event) => setForm({ ...form, is_active: event.target.checked })}
                                    className="h-5 w-5 rounded border-green-300 bg-green-50 text-green-400 focus:ring-green-400"
                                />
                                <label className="text-sm text-green-600">Actif</label>
                            </div>
                            <div className="flex gap-3">
                                <button type="submit" className="rounded-3xl bg-green-600 px-5 py-3 font-semibold text-slate-950 hover:bg-green-500">
                                    Enregistrer
                                </button>
                                <button type="button" onClick={clearForm} className="rounded-3xl border border-green-300 bg-green-50 px-5 py-3 text-green-800 hover:border-green-500">
                                    Annuler
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                <div className="w-2/3 rounded-3xl border border-green-400 bg-white p-5 shadow-xl shadow-slate-950/20 flex flex-col">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                        <h3 className="text-xl font-semibold text-green-900">Liste des consommables</h3>
                        <div className="w-full sm:w-1/2">
                            <label className="text-sm text-green-600">Recherche</label>
                            <input
                                type="search"
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                placeholder="Rechercher par nom"
                                className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                            />
                        </div>
                    </div>

                    <div className="mt-4 overflow-y-auto rounded-3xl border border-green-200">
                        <table className="min-w-full text-sm">
                            <thead className="bg-green-50 text-xs uppercase tracking-wide text-green-700">
                                <tr>
                                    <th className="px-4 py-3 text-left">Nom</th>
                                    <th className="px-4 py-3 text-left">Stock</th>
                                    <th className="px-4 py-3 text-left">Seuil</th>
                                    <th className="px-4 py-3 text-left">Unité</th>
                                    <th className="px-4 py-3 text-left">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(filteredConsumables || []).length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-4 py-6 text-center text-sm text-green-600">
                                            Aucun consommable enregistré.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredConsumables.map((consumable) => (
                                        <tr key={consumable.id} className="border-t border-green-100 hover:bg-green-50/60">
                                            <td className="px-4 py-3 font-medium text-slate-800">{consumable.name}</td>
                                            <td className="px-4 py-3 text-slate-700">{formatLocaleNumber(consumable.stock, 0)}</td>
                                            <td className="px-4 py-3 text-slate-700">{formatLocaleNumber(consumable.threshold, 0)}</td>
                                            <td className="px-4 py-3 text-slate-700">{consumable.unit?.name || '—'}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-2">
                                                    <button type="button" onClick={() => setSelectedId(consumable.id)} className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800 hover:bg-green-200">
                                                        Modifier
                                                    </button>
                                                    <button type="button" onClick={() => onDeleteConsumable?.(consumable)} className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-200">
                                                        Supprimer
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
